/**
 * PostHog analytics: canonical events, standard properties, meeting context.
 * entry_path: slack when URL has isSlack=true, else dashboard (per product spec).
 */

import posthog from "posthog-js";
import { momentumStateKey } from "@/lib/momentum-badge";

/** Canonical section display names (exact strings for reporting) */
export const ANALYTICS_SECTION = {
  COMPANY_SNAPSHOT: "Company Snapshot",
  SIGNALS_PRIORITIES: "Signals & Priorities",
  MEETING_ATTENDEES: "Meeting attendees",
  RITHUM_ALIGNS: "How Rithum Aligns to Their Priorities",
  LEADERSHIP: "Leadership Team & Focus Areas",
  MARKETPLACE: "Marketplace Search Results",
  PREVIOUS_CONVERSATIONS: "Previous Conversations",
  BIG_DECISION: "What big decision does the client want to make?",
  LITTLE_DECISION: "What little decision does the client want to make?",
  DETOURS: "Detours",
} as const;

export type EntryPath = "slack" | "dashboard" | "direct";

export type GlobalAnalyticsProps = {
  org_id?: string;
  workspace_id?: string;
  user_id?: string;
  user_role?: string;
  entry_path?: EntryPath;
  environment?: string;
  session_id?: string;
};

export type MeetingAnalyticsSnapshot = {
  meeting_id: number;
  step?: number;
  entry_path: EntryPath;
  meeting_name?: string;
  account_id?: number;
  meeting_type?: string;
  sales_stage?: string;
};

let globalAnalyticsProps: GlobalAnalyticsProps = {};
let meetingSnapshot: MeetingAnalyticsSnapshot | null = null;

export function registerAnalyticsGlobals(props: Partial<GlobalAnalyticsProps>) {
  globalAnalyticsProps = { ...globalAnalyticsProps, ...props };
}

export function setMeetingAnalyticsSnapshot(snapshot: MeetingAnalyticsSnapshot | null) {
  meetingSnapshot = snapshot;
}

export function getMeetingAnalyticsSnapshot(): MeetingAnalyticsSnapshot | null {
  return meetingSnapshot;
}

export function parseIsSlack(searchParams: URLSearchParams | null): boolean {
  if (!searchParams) return false;
  const v = searchParams.get("isSlack");
  return v === "true" || v === "1" || v === "True";
}

/** Slack CTA links use isSlack=true; otherwise treat as dashboard. */
export function entryPathFromSearchParams(searchParams: URLSearchParams | null): EntryPath {
  if (parseIsSlack(searchParams)) return "slack";
  const explicit = searchParams?.get("entry_path");
  if (explicit === "direct") return "direct";
  return "dashboard";
}

export function canonicalSectionForStep(
  sectionKey: string,
  sectionTitle: string,
  tabId: string
): string {
  const k = (sectionKey || "").toLowerCase();
  if (tabId === "tab_2" || k === "historical_context") {
    return ANALYTICS_SECTION.PREVIOUS_CONVERSATIONS;
  }
  const map: Record<string, string> = {
    company_snapshot: ANALYTICS_SECTION.COMPANY_SNAPSHOT,
    signals_priorities: ANALYTICS_SECTION.SIGNALS_PRIORITIES,
    meeting_attendees: ANALYTICS_SECTION.MEETING_ATTENDEES,
    rhithum_aligns: ANALYTICS_SECTION.RITHUM_ALIGNS,
    rithum_aligns: ANALYTICS_SECTION.RITHUM_ALIGNS,
    leadership_team: ANALYTICS_SECTION.LEADERSHIP,
    marketplace_presence: ANALYTICS_SECTION.MARKETPLACE,
  };
  if (map[k]) return map[k];
  const t = (sectionTitle || "").trim();
  if (/^marketplace search results/i.test(t)) return ANALYTICS_SECTION.MARKETPLACE;
  return t || ANALYTICS_SECTION.COMPANY_SNAPSHOT;
}

function sessionId(): string | undefined {
  try {
    return typeof posthog.get_session_id === "function"
      ? posthog.get_session_id()
      : undefined;
  } catch {
    return undefined;
  }
}

function buildPayload(
  props: Record<string, unknown>,
  includeMeeting: boolean
): Record<string, unknown> {
  const env =
    globalAnalyticsProps.environment ??
    (typeof process !== "undefined" ? process.env.NODE_ENV : undefined);
  const base: Record<string, unknown> = {
    ...globalAnalyticsProps,
    environment: env,
    session_id: globalAnalyticsProps.session_id ?? sessionId(),
    ...props,
  };
  if (includeMeeting && meetingSnapshot) {
    Object.assign(base, {
      meeting_id: meetingSnapshot.meeting_id,
      ...(meetingSnapshot.meeting_name != null && {
        meeting_name: meetingSnapshot.meeting_name,
      }),
      ...(meetingSnapshot.account_id != null && {
        account_id: meetingSnapshot.account_id,
      }),
      ...(meetingSnapshot.meeting_type != null && {
        meeting_type: meetingSnapshot.meeting_type,
      }),
      ...(meetingSnapshot.sales_stage != null && {
        sales_stage: meetingSnapshot.sales_stage,
      }),
    });
  }
  return base;
}

export function capture(
  event: string,
  props: Record<string, unknown> = {},
  options?: { includeMeeting?: boolean }
) {
  try {
    const includeMeeting = options?.includeMeeting !== false;
    posthog.capture(event, buildPayload(props, includeMeeting));
  } catch {
    console.error("Failed to capture event:", event, props);
  }
}

export function captureGlobal(event: string, props: Record<string, unknown> = {}) {
  capture(event, props, { includeMeeting: false });
}

/** MAP PDF download funnel — use on export_clicked / export_succeeded / export_failed / export_blocked. */
export const MAP_EXPORT_SOURCE = {
  PREVIOUS_CALL_STEP2: "Previous call (step 2)",
  PREVIOUS_CALL_HISTORY: "Previous call (History)",
  CURRENT_CALL_STEP3: "Current call (step 3)",
  CURRENT_CALL_HEADER: "Current call (header)",
} as const;

export type MapExportSource = (typeof MAP_EXPORT_SOURCE)[keyof typeof MAP_EXPORT_SOURCE];

/** Clerk / app role collapsed for Call History nav reporting (rep | manager). */
export type CallHistoryNavUserRole = "rep" | "manager";

export function callHistoryNavUserRoleFromMetadata(
  publicMetadata: Record<string, unknown> | undefined | null
): CallHistoryNavUserRole {
  const raw = String((publicMetadata as { role?: string })?.role ?? "").toLowerCase();
  if (raw.includes("manager")) return "manager";
  return "rep";
}

export type DateFilterView = "call_history" | "account_detail" | "manager_dashboard";

export function captureDateFilterChanged(
  view: DateFilterView,
  range: { startDate: string; endDate: string } | null
) {
  captureGlobal("date_filter_changed", {
    view,
    start_date: range?.startDate ?? "",
    end_date: range?.endDate ?? "",
  });
}

export type MomentumBadgeClickSource = "step2_past_call";

export type ManagerDashboardView = "team" | "account";

export type AccountFilterType = "rep" | "momentum_state" | "direction";

export type CoachingEmailLevel = "rep" | "account";

export function captureMomentumBadgeClicked(props: {
  account_id?: number;
  meeting_id?: number;
  momentum_label?: string | null;
  momentum_direction?: string | null;
  momentum_state?: string | null;
}) {
  const { momentum_label, momentum_direction, momentum_state, ...rest } = props;
  const normalizedState =
    momentum_state?.trim() ||
    momentumStateKey(momentum_label) ||
    momentum_label?.trim() ||
    undefined;
  captureGlobal("momentum_badge_clicked", {
    ...rest,
    source: "step2_past_call" satisfies MomentumBadgeClickSource,
    step: 2,
    momentum_label: momentum_label ?? undefined,
    momentum_direction: momentum_direction ?? undefined,
    momentum_state: normalizedState,
  });
}

export function captureManagerDashboardOpened(default_view: ManagerDashboardView) {
  captureGlobal("manager_dashboard_opened", { default_view });
}

export function captureViewToggleSwitched(
  from_view: ManagerDashboardView,
  to_view: ManagerDashboardView,
) {
  captureGlobal("view_toggle_switched", { from_view, to_view });
}

export function captureRepDetailOpened(rep_id: string) {
  captureGlobal("rep_detail_opened", { rep_id });
}

export function captureAccountDetailOpened(account_id: number) {
  captureGlobal("account_detail_opened", { account_id });
}

export function captureKnowMoreClicked(rep_id: string, total_blocked_accounts: number) {
  captureGlobal("know_more_clicked", { rep_id, total_blocked_accounts });
}

export function captureAccountFilterApplied(filter_type: AccountFilterType, filter_value: string) {
  captureGlobal("account_filter_applied", { filter_type, filter_value });
}

export function captureCoachingEmailDrafted(props: {
  level: CoachingEmailLevel;
  rep_id: string;
  account_id?: number;
}) {
  captureGlobal("coaching_email_drafted", props);
}

export function captureCoachingEmailCopied(props: {
  level: CoachingEmailLevel;
  rep_id: string;
  account_id?: number;
}) {
  captureGlobal("coaching_email_copied", props);
}

export function inferLoginMethod(user: {
  externalAccounts?: Array<{ provider?: string }>;
  passwordEnabled?: boolean;
}): "sso_google" | "sso_microsoft" | "otp" | "password" {
  const accounts = user?.externalAccounts || [];
  for (const a of accounts) {
    const p = (a.provider || "").toLowerCase();
    if (p.includes("google")) return "sso_google";
    if (p.includes("microsoft")) return "sso_microsoft";
  }
  if (user?.passwordEnabled) return "password";
  return "otp";
}

export function trackDetailsDrawer(
  type: "signal" | "alignment" | "suggestion" | "meeting" | "engagement",
  data: {
    suggestion_id?: string | number;
    signal_id?: number;
    alignment_id?: string | number;
    section?: string;
  }
) {
  const snap = meetingSnapshot;
  capture(
    "details_clicked",
    {
      drawer_type: type,
      meeting_id: snap?.meeting_id,
      step: snap?.step,
      entry_path: snap?.entry_path,
      suggestion_id: data.suggestion_id,
      signal_id: data.signal_id,
      alignment_id: data.alignment_id,
      section: data.section,
    },
    { includeMeeting: true }
  );
}

export function trackStep1Step2SuggestionAction(
  action: string,
  prevMeta: {
    is_liked?: boolean | null;
    is_disliked?: boolean | null;
    added_to_meeting?: boolean;
  },
  nextMeta: {
    is_liked: boolean;
    is_disliked: boolean;
    added_to_meeting: boolean;
  },
  ctx: {
    meeting_id: number;
    section: string;
    step: 1 | 2;
    account_id?: number;
    entry_path?: EntryPath;
  }
) {
  const base = {
    meeting_id: ctx.meeting_id,
    section: ctx.section,
    step: ctx.step,
    entry_path: ctx.entry_path,
    account_id: ctx.account_id,
  };

  if (action === "add_to_meeting") {
    if (nextMeta.added_to_meeting && !prevMeta.added_to_meeting) {
      capture("AI_suggestion_added", {
        ...base,
        source: "lighthouse_suggestion",
        suggestion_type: undefined,
      });
    } else if (!nextMeta.added_to_meeting && prevMeta.added_to_meeting) {
      capture("AI_suggestion_removed", {
        ...base,
        source: "lighthouse_suggestion",
      });
    }
  } else if (action === "like_suggestion" && nextMeta.is_liked && !prevMeta.is_liked) {
    capture("AI_suggestion_liked", { ...base });
  } else if (
    action === "dislike_suggestion" &&
    nextMeta.is_disliked &&
    !prevMeta.is_disliked
  ) {
    capture("AI_suggestion_disliked", { ...base });
  }
}

export type SuggestionActionPayload = {
  actionId: string;
  action: string;
  suggestionId: string;
  sectionKey: string;
  sectionTitle: string;
  tabId: string;
};

/** PostHog: error / not-found pages (routing + runtime errors). */
export type ErrorPageScope = "app" | "meetings";

export type ErrorPageCategory = "route_not_found" | "runtime_error";

export type ErrorPageAnalyticsPayload = {
  error_category: ErrorPageCategory;
  page_scope: ErrorPageScope;
  /** Stable event name for PostHog funnels */
  page_kind:
    | "not_found_global"
    | "not_found_meeting"
    | "runtime_error_global"
    | "runtime_error_meeting";
  page_path: string;
  page_search?: string;
  full_url?: string;
  referrer?: string;
  referrer_host?: string;
  /** From URL when present (meetings flow) */
  entry_path?: EntryPath;
  /** Raw meeting id segment from route, when applicable */
  meeting_id_param?: string;
  /** Parsed numeric id when valid */
  meeting_prep_id?: number;
  error_name?: string;
  error_message?: string;
  error_digest?: string;
};

function referrerHost(referrer: string): string | undefined {
  try {
    return new URL(referrer).host;
  } catch {
    return undefined;
  }
}

/**
 * Fire once when a user lands on a global or meeting 404 UI.
 */
export function captureRouteNotFoundPage(
  scope: ErrorPageScope,
  ctx: {
    pathname: string;
    search: string;
    meetingIdParam?: string;
  }
): void {
  const sp = new URLSearchParams(ctx.search || "");
  const pageKind: ErrorPageAnalyticsPayload["page_kind"] =
    scope === "meetings" ? "not_found_meeting" : "not_found_global";
  let meetingPrepId: number | undefined;
  if (ctx.meetingIdParam) {
    const n = parseInt(ctx.meetingIdParam, 10);
    if (!Number.isNaN(n) && n > 0) meetingPrepId = n;
  }
  const payload: ErrorPageAnalyticsPayload = {
    error_category: "route_not_found",
    page_scope: scope,
    page_kind: pageKind,
    page_path: ctx.pathname,
    page_search: ctx.search || undefined,
    full_url: typeof window !== "undefined" ? window.location.href : undefined,
    referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
    referrer_host:
      typeof document !== "undefined" && document.referrer
        ? referrerHost(document.referrer)
        : undefined,
    entry_path: entryPathFromSearchParams(sp),
    ...(ctx.meetingIdParam != null && { meeting_id_param: ctx.meetingIdParam }),
    ...(meetingPrepId != null && { meeting_prep_id: meetingPrepId }),
  };
  captureGlobal("error_page_viewed", payload as Record<string, unknown>);
}

/**
 * Fire once when Next.js error boundary renders (global or meeting segment).
 */
export function captureRuntimeErrorPage(
  scope: ErrorPageScope,
  ctx: {
    pathname: string;
    search: string;
    meetingIdParam?: string;
    error: Error & { digest?: string };
  }
): void {
  const sp = new URLSearchParams(ctx.search || "");
  const pageKind: ErrorPageAnalyticsPayload["page_kind"] =
    scope === "meetings" ? "runtime_error_meeting" : "runtime_error_global";
  let meetingPrepId: number | undefined;
  if (ctx.meetingIdParam) {
    const n = parseInt(ctx.meetingIdParam, 10);
    if (!Number.isNaN(n) && n > 0) meetingPrepId = n;
  }
  const payload: ErrorPageAnalyticsPayload = {
    error_category: "runtime_error",
    page_scope: scope,
    page_kind: pageKind,
    page_path: ctx.pathname,
    page_search: ctx.search || undefined,
    full_url: typeof window !== "undefined" ? window.location.href : undefined,
    referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
    referrer_host:
      typeof document !== "undefined" && document.referrer
        ? referrerHost(document.referrer)
        : undefined,
    entry_path: entryPathFromSearchParams(sp),
    error_name: ctx.error.name,
    error_message: ctx.error.message,
    ...(ctx.error.digest != null && { error_digest: ctx.error.digest }),
    ...(ctx.meetingIdParam != null && { meeting_id_param: ctx.meetingIdParam }),
    ...(meetingPrepId != null && { meeting_prep_id: meetingPrepId }),
  };
  captureGlobal("error_page_viewed", payload as Record<string, unknown>);
}

export function trackStep3SuggestionAction(
  action: "like_suggestion" | "dislike_suggestion" | "add_to_meeting",
  prevMeta: { is_liked?: boolean; is_disliked?: boolean; added_to_meeting?: boolean },
  nextMeta: { is_liked: boolean; is_disliked: boolean; added_to_meeting: boolean },
  ctx: {
    section: string;
    suggestion_type: "big_decision" | "little_decision" | "detours";
  }
) {
  const base = {
    section: ctx.section,
    suggestion_type: ctx.suggestion_type,
    step: 3,
  };

  if (action === "add_to_meeting") {
    if (nextMeta.added_to_meeting && !prevMeta.added_to_meeting) {
      capture("AI_suggestion_added", { ...base, source: "lighthouse_suggestion" });
    } else if (!nextMeta.added_to_meeting && prevMeta.added_to_meeting) {
      capture("AI_suggestion_removed", { ...base, source: "lighthouse_suggestion" });
    }
  } else if (action === "like_suggestion" && nextMeta.is_liked && !prevMeta.is_liked) {
    capture("AI_suggestion_liked", { ...base });
  } else if (
    action === "dislike_suggestion" &&
    nextMeta.is_disliked &&
    !prevMeta.is_disliked
  ) {
    capture("AI_suggestion_disliked", { ...base });
  }
}
